import { Injectable, inject, signal, effect, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, filter, interval, of, Subscription, switchMap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { CoreService } from './core.service';
import { PaysheetProcessService } from 'src/app/apps/empresas/modules/nomina/novedades-ausencias/services/paysheet-process.service';
import { GestionRetirosService } from 'src/app/apps/empresas/modules/gestion-retiros/services/gestion-retiros.service';
import { INomActivePreNomina } from 'src/app/apps/empresas/modules/nomina/novedades-ausencias/interfaces/INomActivePreNomina';
import { RolesMenuService } from 'src/app/apps/nodo/modules/administracion/services/roles-menus.service';

/** Intervalo de polling en milisegundos. */
const POLL_INTERVAL_MS = 10_000;

/** ID del portal Empresas. */
const PORTAL_EMPRESAS = '2';

/** Prefijo de las rutas del portal Empresas. */
const PREFIJO_RUTA_EMPRESAS = '/app/empresas';

/**
 * Menú que habilita el monitor. La campanita avisa de pre-nómina y de paz y salvo,
 * y ambos flujos se gestionan desde "Novedades y Ausencias"; la bandeja de paz y
 * salvo no tiene menú propio, se entra desde ahí. Si el rol no tiene este menú, no
 * hay nada que notificarle y el sondeo no arranca nunca.
 */
const RUTA_MENU_HABILITADOR = 'app/empresas/nomina/novedades';

/** Máximo de notificaciones de pre-nómina conservadas en memoria. */
const MAX_NOTIFICATIONS = 20;

/**
 * Notificación de un proceso de pre-nómina finalizado, lista para mostrarse
 * en la campanita del topbar.
 */
export interface PreNominaNotification {
    /** Identificador único de la notificación. */
    id: string;
    /** Estado final del proceso. */
    estado: 'Completado' | 'Error';
    /**
     * Título legible. Para pre-nómina: "Pre-nómina completada" / "Pre-nómina con error".
     * Para liquidación de contratos (paz y salvo): "Paz y salvo generado" /
     * "Error al generar el paz y salvo".
     */
    title: string;
    /** Detalle: convenio y corte. */
    subtitle: string;
    /** Hora de finalización en formato HH:mm. */
    time: string;
    /** Código de convenio (para navegación). */
    agreement: string | null;
    /** Fecha de corte yyyyMMdd (para navegación). */
    dateCut: string | null;
    /**
     * A dónde lleva el clic. La campanita mezcla avisos de pre-nómina y de paz y
     * salvo, y son pantallas distintas: sin esto, un aviso de paz y salvo abría
     * Novedades y ausencias, que no tiene nada que ver.
     */
    destino: 'prenomina' | 'pazysalvo';
}

@Injectable({ providedIn: 'root' })
export class PreNominaMonitorService implements OnDestroy {
    private readonly coreService = inject(CoreService);
    private readonly paysheetProcessService = inject(PaysheetProcessService);
    private readonly gestionRetiros = inject(GestionRetirosService);
    private readonly toastr = inject(ToastrService);
    private readonly router = inject(Router);
    private readonly rolesMenuService = inject(RolesMenuService);

    /**
     * Si la pantalla que se está viendo pertenece al portal Empresas.
     *
     * El portal seleccionado se persiste en localStorage, así que sobrevive a la
     * navegación: quien entró alguna vez a Empresas seguía sondeando desde
     * cualquier pantalla del Nodo. La ruta activa es la única señal honesta de
     * dónde está el usuario ahora.
     */
    private readonly enPortalEmpresas = signal<boolean>(false);

    /**
     * Si el rol tiene el menú que habilita el monitor. null = todavía no se consultó.
     * Se resuelve una sola vez por sesión y se conserva: es información del rol, no
     * de la empresa seleccionada.
     */
    private readonly tienePermiso = signal<boolean | null>(null);

    /** Evita repetir la consulta de menús mientras la primera está en vuelo. */
    private permisoEnConsulta = false;

    /** Estado activo de la pre-nómina. null = sin información disponible. */
    readonly activePreNomina = signal<INomActivePreNomina | null>(null);

    /** Historial de notificaciones de procesos de pre-nómina finalizados. */
    readonly notifications = signal<PreNominaNotification[]>([]);

    /** Cantidad de notificaciones sin leer (badge de la campanita). */
    readonly unreadCount = signal<number>(0);

    /**
     * Paz y salvo en la bandeja de la empresa seleccionada. Vive aquí y no en la
     * pantalla de retiros porque el contador se muestra en una pestaña que puede
     * estar cerrada: el panel de la bandeja se destruye al cambiar de pestaña y
     * dejaría de emitirlo. Este sondeo ya se hacía para las notificaciones, así
     * que el contador no cuesta una petición extra.
     */
    readonly liquidacionesPendientes = signal<number>(0);

    /**
     * Se incrementa cada vez que un paz y salvo sale de la bandeja: se aprobó y
     * cerró, o se rechazó. La pestaña de cerrados lo observa para refrescarse sola;
     * antes había que recargar la página para ver el que acababa de cerrarse.
     */
    readonly liquidacionFinalizada = signal<number>(0);

    /** Suscripción al intervalo de polling. */
    private pollSub: Subscription | null = null;

    /**
     * Guarda el último estado `activo` para detectar la transición
     * activo=true → estado='Completado' y disparar la notificación de fin.
     */
    private wasActive = false;

    constructor() {
        this.enPortalEmpresas.set(this.esRutaEmpresas(this.router.url));
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((e) => this.enPortalEmpresas.set(this.esRutaEmpresas(e.urlAfterRedirects)));

        // Reactivo: arranca/detiene polling según portal, empresa, pantalla y permiso.
        effect(() => {
            const portalId = this.coreService.getIdPortalSignal()();
            const company = this.coreService.getCompanySignal()();
            const nit = company?.id ?? '';
            const enEmpresas = this.enPortalEmpresas();
            const permiso = this.tienePermiso();

            const contexto = portalId === PORTAL_EMPRESAS && nit.trim().length > 0 && enEmpresas;

            if (!contexto) {
                this.detener();
                return;
            }

            // El permiso se consulta al primer contexto válido, no al arrancar la app:
            // así un usuario que nunca entra a Empresas no gasta ni esa petición.
            if (permiso === null) {
                this.resolverPermiso();
                this.detener();
                return;
            }

            if (!permiso) {
                this.detener();
                return;
            }

            this.startPolling(nit);
        });
    }

    private detener(): void {
        this.stopPolling();
        this.activePreNomina.set(null);
        this.wasActive = false;
    }

    private esRutaEmpresas(url: string): boolean {
        return (url ?? '').startsWith(PREFIJO_RUTA_EMPRESAS);
    }

    /**
     * Consulta los menús del portal Empresas del rol y decide si el monitor aplica.
     * Ante un fallo de red se asume que NO tiene permiso: un sondeo que no debería
     * existir es peor que una campanita que no aparece, y la próxima navegación a
     * Empresas vuelve a intentarlo.
     */
    private resolverPermiso(): void {
        if (this.permisoEnConsulta) return;
        this.permisoEnConsulta = true;

        this.rolesMenuService
            .getMenusbyId(PORTAL_EMPRESAS)
            .pipe(catchError(() => of(null)))
            .subscribe((resp) => {
                this.permisoEnConsulta = false;
                if (!resp) {
                    this.tienePermiso.set(false);
                    return;
                }
                this.tienePermiso.set(this.contieneMenuHabilitador(resp.data ?? []));
            });
    }

    /** Busca la ruta habilitadora en el árbol de menús (hasta tres niveles, como el sidebar). */
    private contieneMenuHabilitador(menus: any[]): boolean {
        for (const menu of menus ?? []) {
            const ruta = (menu?.urlRuta ?? '').trim();
            if (ruta === RUTA_MENU_HABILITADOR) return true;
            if (menu?.items?.length && this.contieneMenuHabilitador(menu.items)) return true;
        }
        return false;
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    /**
     * Procesos de paz y salvo que se vieron trabajando (id → empleado). El
     * recálculo, el rechazo y el cierre desde la bandeja NO pasan por la
     * pre-nómina, así que el monitor de arriba no los ve: sin esto el usuario
     * tenía que quedarse recargando la bandeja para saber cuándo terminaban.
     */
    private liquidacionesEnCurso = new Map<number, string>();

    /**
     * Hitos de paz y salvo ya avisados, PERSISTIDOS en localStorage.
     *
     * La memoria en variables no sirve para deduplicar avisos: el effect que
     * arranca el polling se re-evalúa (cambio de empresa, navegación, recarga)
     * y cada arranque borraba lo "ya visto" y marcaba el siguiente barrido como
     * foto inicial. Si un proceso terminaba durante ese reinicio, aparecía ya
     * quieto en un barrido-foto y el aviso no salía nunca — reportado el
     * 10/08/2026: el paz y salvo terminó y la campanita se quedó muda.
     *
     * Claves: "<processId>:pendiente" (llegó a la bandeja listo para aprobar) y
     * "<processId>:final" (salió de la bandeja: se cerró o se rechazó). Cada hito
     * se avisa UNA vez en la vida del proceso, sobreviva lo que sobreviva el
     * servicio. El valor guarda cuándo y a quién, para el aviso de cierre.
     */
    private static readonly AVISADOS_KEY = 'ps-liq-avisados-v1';

    /** Hitos con más de 30 días se podan: sus procesos ya son historia. */
    private static readonly AVISADOS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

    private leerAvisados(): Record<string, { t: number; n?: string }> {
        try {
            return JSON.parse(localStorage.getItem(PreNominaMonitorService.AVISADOS_KEY) ?? '{}');
        } catch {
            return {};
        }
    }

    private marcarAvisado(
        avisados: Record<string, { t: number; n?: string }>,
        clave: string,
        nombres?: string
    ): void {
        avisados[clave] = { t: Date.now(), n: nombres };
        const limite = Date.now() - PreNominaMonitorService.AVISADOS_TTL_MS;
        for (const k of Object.keys(avisados)) {
            if ((avisados[k]?.t ?? 0) < limite) delete avisados[k];
        }
        try {
            localStorage.setItem(PreNominaMonitorService.AVISADOS_KEY, JSON.stringify(avisados));
        } catch {
            // Sin localStorage (modo privado estricto) se degrada a avisar de más,
            // nunca a callar: preferible un aviso repetido a otro que no llega.
        }
    }

    /**
     * Revisa la bandeja y notifica los cambios del paz y salvo. Este es el ÚNICO
     * canal que avisa sobre liquidaciones de contrato: el pipeline dispara varias
     * preliquidaciones internas (la del período y la del paz y salvo) y notificar
     * por cada una llenaba la campanita de avisos repetidos para una sola
     * liquidación. Aquí se notifica por evento del negocio, no por cálculo.
     */
    private vigilarLiquidaciones(nit: string): void {
        this.gestionRetiros
            // Silencioso: es un sondeo de fondo, no debe mover el loader global.
            .liquidacionesPendientes(nit, true)
            .pipe(catchError(() => of(null)))
            .subscribe((resp) => {
                // Respuesta fallida (resp null): no se toca nada, para no borrar la
                // insignia ni marcar hitos por un corte de red.
                if (!resp) return;

                const filas = resp.data ?? [];
                const vistos = new Set<number>();
                const avisados = this.leerAvisados();

                // Solo los que de verdad esperan la aprobación del cliente. La bandeja
                // trae también los EN CURSO y los detenidos; contarlos aquí hacía que un
                // cliente SIN aprobación manual viera "1 paz y salvo pendientes de
                // aprobación" mientras su proceso simplemente se calculaba (reportado
                // 11/08/2026 con el 66118 de 811007991, que jamás iba a pedir aprobación).
                this.liquidacionesPendientes.set(
                    filas.filter((f) => !f.enProceso && !f.detalleError).length
                );

                for (const fila of filas) {
                    vistos.add(fila.processId);
                    if (fila.enProceso) {
                        this.liquidacionesEnCurso.set(fila.processId, fila.empleadosNombres);
                        continue;
                    }

                    const veniaTrabajando = this.liquidacionesEnCurso.delete(fila.processId);
                    const clavePendiente = `${fila.processId}:pendiente`;
                    const yaAvisado = clavePendiente in avisados;

                    // Cada hito se avisa una vez en la vida del proceso; solo un
                    // recálculo que ACABA de terminar (venía trabajando) repite aviso,
                    // porque eso sí es un evento nuevo.
                    if (yaAvisado && !veniaTrabajando) continue;

                    const conProblema = (fila.estado ?? '').startsWith('Requiere atención');
                    const titulo = conProblema
                        ? 'El paz y salvo necesita tu atención'
                        : yaAvisado
                          ? 'Paz y salvo actualizado'
                          : 'Paz y salvo listo para aprobar';
                    this.pushLiquidacionNotif(
                        conProblema ? 'Error' : 'Completado',
                        titulo,
                        `${fila.empleadosNombres} — corte ${fila.dateCut}`
                    );
                    this.marcarAvisado(avisados, clavePendiente, fila.empleadosNombres);
                }

                // Salió de la bandeja: se aprobó y cerró, o se rechazó. Se detecta por
                // los hitos persistidos, no por la memoria de este servicio: así el
                // aviso llega aunque el cierre ocurriera con la pestaña cerrada.
                for (const [processId, empleados] of [...this.liquidacionesEnCurso]) {
                    if (!vistos.has(processId)) this.liquidacionesEnCurso.delete(processId);
                    else continue;
                    // Sin hito 'pendiente' no hay clave estable: se marca aquí mismo
                    // para que el cierre de abajo lo procese una sola vez.
                    if (!(`${processId}:pendiente` in avisados))
                        this.marcarAvisado(avisados, `${processId}:pendiente`, empleados);
                }

                for (const clave of Object.keys(avisados)) {
                    const m = /^(\d+):pendiente$/.exec(clave);
                    if (!m) continue;
                    const processId = Number(m[1]);
                    if (vistos.has(processId)) continue;

                    const claveFinal = `${processId}:final`;
                    if (claveFinal in avisados) continue;

                    this.pushLiquidacionNotif(
                        'Completado',
                        'Paz y salvo finalizado',
                        avisados[clave]?.n ?? 'Consulta el detalle en la bandeja'
                    );
                    this.liquidacionFinalizada.update((n) => n + 1);
                    this.marcarAvisado(avisados, claveFinal);
                }
            });
    }

    /** Agrega una notificación de paz y salvo a la campanita. */
    private pushLiquidacionNotif(
        estado: 'Completado' | 'Error',
        title: string,
        subtitle: string
    ): void {
        const notif: PreNominaNotification = {
            id: `${Date.now()}-liq`,
            estado,
            title,
            subtitle,
            time: this.formatTime(new Date()),
            agreement: null,
            dateCut: null,
            destino: 'pazysalvo'
        };
        this.notifications.update((list) => [notif, ...list].slice(0, MAX_NOTIFICATIONS));
        this.unreadCount.update((c) => c + 1);
        if (estado === 'Completado') this.toastr.success(subtitle, title);
        else this.toastr.warning(subtitle, title);
    }


    /**
     * Fuerza una consulta inmediata del estado, sin esperar al siguiente tick del
     * polling. Útil tras encolar una pre-nómina para que el indicador del header
     * y la barra de la página se sincronicen de inmediato.
     */
    refreshNow(): void {
        const nit = this.coreService.getCompanySignal()()?.id ?? '';
        if (nit.trim().length > 0) {
            this.pollOnce(nit);
        }
    }

    // ─── Polling ──────────────────────────────────────────────────────────────

    private startPolling(nit: string): void {
        // Si ya hay un polling activo para el mismo NIT, no duplicar.
        // Al cambiar de empresa el effect se vuelve a evaluar, deteniendo el anterior.
        this.stopPolling();

        // Solo se limpia lo transitorio. La deduplicación de avisos vive en
        // localStorage y NO se resetea aquí: cada reinicio de este método borraba
        // la memoria y el aviso de un proceso terminado en ese hueco no salía nunca.
        this.liquidacionesEnCurso.clear();
        this.liquidacionesPendientes.set(0);

        // Disparo inmediato + cada POLL_INTERVAL_MS.
        this.pollOnce(nit);

        this.pollSub = interval(POLL_INTERVAL_MS)
            .pipe(
                switchMap(() => {
                    // El recálculo, el rechazo y el cierre desde la bandeja no pasan
                    // por la pre-nómina: se vigilan aparte en el mismo tick.
                    this.vigilarLiquidaciones(nit);
                    return this.fetchActivePreNomina(nit);
                })
            )
            .subscribe((result) => this.handlePollResult(result));
    }

    private stopPolling(): void {
        this.pollSub?.unsubscribe();
        this.pollSub = null;
    }

    private pollOnce(nit: string): void {
        this.fetchActivePreNomina(nit).subscribe((result) => this.handlePollResult(result));
        this.vigilarLiquidaciones(nit);
    }

    private fetchActivePreNomina(nit: string) {
        return this.paysheetProcessService
            .getActivePreNomina(nit)
            .pipe(catchError(() => of(null)));
    }

    // ─── Manejo del resultado ─────────────────────────────────────────────────

    private handlePollResult(response: { success: boolean; data: INomActivePreNomina; message: string } | null): void {
        if (!response?.success || !response.data) {
            // Error de red o respuesta vacía: no limpiar el estado visible.
            return;
        }

        const data = response.data;
        this.activePreNomina.set(data);

        // Detectar transición: proceso que estaba activo y acaba de terminar.
        if (this.wasActive && !data.activo) {
            if (data.estado === 'Completado') {
                this.pushNotification(data, 'Completado');
                this.notifyCompleted(data);
            } else if (data.estado === 'Error') {
                this.pushNotification(data, 'Error');
                this.notifyError(data);
            }
        }

        this.wasActive = data.activo;
    }

    // ─── Notificaciones de la campanita ───────────────────────────────────────

    /** Marca todas las notificaciones como leídas (resetea el badge). */
    markNotificationsRead(): void {
        this.unreadCount.set(0);
    }

    /** Elimina todas las notificaciones del historial. */
    clearNotifications(): void {
        this.notifications.set([]);
        this.unreadCount.set(0);
    }

    /**
     * Crea una notificación de proceso finalizado y la agrega al historial,
     * incrementando el contador de no leídas.
     *
     * El título depende de `data.esLiquidacionContrato`: si el proceso es una
     * liquidación de contratos (retiro → paz y salvo) el cliente NUNCA debe ver la
     * palabra "nómina", así que se usan los textos de paz y salvo. En cualquier
     * otro caso se mantienen, sin cambios, los textos de pre-nómina de siempre.
     */
    private pushNotification(data: INomActivePreNomina, estado: 'Completado' | 'Error'): void {
        const convenio = data.nombreConvenio ?? data.agreement ?? 'Convenio';
        const corte = this.formatDateCut(data.dateCut);

        const title = data.esLiquidacionContrato
            ? (estado === 'Completado' ? 'Paz y salvo generado' : 'Error al generar el paz y salvo')
            : (estado === 'Completado' ? 'Pre-nómina completada' : 'Pre-nómina con error');

        const notif: PreNominaNotification = {
            id: `${Date.now()}-${data.agreement ?? ''}`,
            estado,
            title,
            subtitle: corte ? `${convenio} — corte ${corte}` : convenio,
            time: this.formatTime(new Date()),
            destino: 'prenomina',
            agreement: data.agreement ?? null,
            dateCut: data.dateCut ?? null
        };

        this.notifications.update((list) => [notif, ...list].slice(0, MAX_NOTIFICATIONS));
        this.unreadCount.update((c) => c + 1);
    }

    /** Convierte una fecha yyyyMMdd a dd/MM/yyyy. Devuelve '' si no aplica. */
    private formatDateCut(raw: string | null | undefined): string {
        if (!raw || !/^\d{8}$/.test(raw)) return raw ?? '';
        return `${raw.slice(6, 8)}/${raw.slice(4, 6)}/${raw.slice(0, 4)}`;
    }

    /** Formatea una hora como HH:mm. */
    private formatTime(date: Date): string {
        const hh = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
    }

    private notifyCompleted(data: INomActivePreNomina): void {
        const convenio = data.nombreConvenio ?? data.agreement ?? '';
        const suffix = convenio ? ` — Convenio ${convenio}` : '';

        if (data.esLiquidacionContrato) {
            this.toastr.success(
                `El paz y salvo se generó correctamente${suffix}.`,
                'Paz y salvo generado',
                { timeOut: 8000, progressBar: true }
            );
            return;
        }

        this.toastr.success(
            `La pre-nómina ha finalizado correctamente${suffix}.`,
            'Pre-nómina completada',
            { timeOut: 8000, progressBar: true }
        );
    }

    private notifyError(data: INomActivePreNomina): void {
        const convenio = data.nombreConvenio ?? data.agreement ?? '';
        const suffix = convenio ? ` — Convenio ${convenio}` : '';

        if (data.esLiquidacionContrato) {
            this.toastr.error(
                `El paz y salvo terminó con error${suffix}.`,
                'Paz y salvo',
                { timeOut: 9000, progressBar: true }
            );
            return;
        }

        this.toastr.error(
            `La pre-nómina terminó con error${suffix}.`,
            'Pre-nómina',
            { timeOut: 9000, progressBar: true }
        );
    }
}
